import React, {memo, useCallback, useMemo, useState} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {CustomizedRange} from "../../../../components/CustomizedRange/RangeComponent"
import {useDispatch, useSelector} from "react-redux"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import {useLocalization} from "../../../../hooks/localization"
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../../components/Select"
import iconScreen from "../../../../assets/images/icons/screen-brightness.png"
import iconScreenSleep from "../../../../assets/images/icons/screen-brightness-sleep.png"
import {setEntityListAction} from "../../../../store/control/actions"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {useParams} from "react-router-dom"
import {GatewayCommand} from "../../../../types/control"
import {useQuestion} from "../../../../hooks/question"
import {useDebounce} from "../../../../hooks/debounce"
import {changeBusyAction} from "../../../../store/ui/actions"
import "./index.scss"

export const ScreenEditScreen = memo(function ScreenEditScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const {id} = useParams<{id: string}>()
    const dispatch = useDispatch()
    const {failure} = useMessage()
    const question = useQuestion()
    const screen = useSelector(state => state.control.screenStateList)
    const favoritesStatus = useSelector(state => state.control.favoritesPage)
    const config = useSelector(state => state.control.config)
    const gatewayList = useSelector(state => state.control.gatewayList)
    const selectedGateway = useMemo(() => gatewayList.find(g => g.id === id), [gatewayList, id])
    const currentRole = useSelector(state => state.ui.role)
    const [screenBrightness, setScreenBrightness] = useState(screen?.screen_standard_brightness)
    const [screenSaverActiveBrightness, setScreenSaverActiveBrightness] = useState(
        screen?.screen_saver_active_brightness
    )

    const saverTimeList = useMemo<SelectItem<number>[]>(
        () => [
            {
                value: 1,
                label: t("customizations.one_minute")
            },
            {
                value: 5,
                label: t("customizations.five_minutes")
            },
            {
                value: 30,
                label: t("customizations.thirty_minutes")
            },
            {
                value: 60,
                label: t("customizations.one_hour")
            }
        ],
        [t]
    )

    const screenSleepTimes = useMemo<SelectItem<number>[]>(
        () => [
            {
                value: 15,
                label: t("customizations.fifteen_second")
            },
            {
                value: 30,
                label: t("customizations.thirty_second")
            },
            {
                value: 60,
                label: t("customizations.one_minute")
            },
            {
                value: 300,
                label: t("customizations.five_minutes")
            },
            {
                value: 1800,
                label: t("customizations.thirty_minutes")
            },
            {
                value: 3600,
                label: t("customizations.one_hour")
            }
        ],
        [t]
    )

    const optionWallpaper = useMemo<SelectItem<string>[]>(
        () => [
            {
                value: "analog-clock",
                label: t("customizations.analog")
            },
            {
                value: "digital-clock",
                label: t("customizations.digital")
            },
            {
                value: "slider",
                label: t("customizations.slide")
            }
        ],
        [t]
    )

    const [selectScreenTime, setSelectScreenTime] = useState<SelectItem<number>>(
        screen.screen_lock_timing_minute == null
            ? saverTimeList[1]
            : saverTimeList.find(timeOption => timeOption.value === screen.screen_lock_timing_minute)
    )

    const [selectScreenSleepTime, setSelectScreenSleepTime] = useState<SelectItem<number>>(
        screen.screen_sleep_second == null
            ? screenSleepTimes[1]
            : screenSleepTimes.find(timeOption => timeOption.value === screen.screen_sleep_second)
    )

    const [selectScreenType, setSelectScreenType] = useState<SelectItem<string>>(
        optionWallpaper.find(screenType => screenType.value === screen.screen_saver_type)
    )

    const [selectScreenSaverTime, setSelectScreenSaverTime] = useState<SelectItem<number>>(() =>
        saverTimeList.find(timeOption => timeOption.value === screen.screen_saver_timing_minute)
    )

    const [pinCode, setPinCode] = useState(screen.screen_lock_status || false)
    const [screenSaver, setScreenSaver] = useState(screen.screen_saver_status || false)
    const [indicatorStatus, setIndicatorStatus] = useState(config?.indicator_status || false)
    const [favoritesItems, setFavoritesItems] = useState<boolean>(favoritesStatus || false)

    const updateScreenBrightness = useDebounce((value: number) => setScreenBrightness(value), 100)
    const updateScreenSleepBrightness = useDebounce((value: number) => setScreenSaverActiveBrightness(value), 100)

    const onScreenBrightnessChange = useCallback(
        (value: number) => updateScreenBrightness(value),
        [updateScreenBrightness]
    )
    const onScreenSleepBrightnessChange = useCallback(
        (value: number) => updateScreenSleepBrightness(value),
        [updateScreenSleepBrightness]
    )

    const onChangeIndicatorStatus = useCallback(() => setIndicatorStatus(p => !p), [])
    const onChangeFavoritesStatus = useCallback(
        () => question(t("after_this_you_need_to_restart_the_device"), () => setFavoritesItems(p => !p)),
        [t, question]
    )

    const onScreenSaverStateChange = useCallback(() => {
        setScreenSaver(!screenSaver)
    }, [screenSaver])

    const onScreenPinCodeStateChange = useCallback(() => {
        setPinCode(!pinCode)
    }, [pinCode])

    const askSaveChanges = useCallback(async () => {
        if (favoritesItems !== favoritesStatus) {
            await rpc.setHomepageItemsByGatewayStatus(favoritesItems)
        }
        if (indicatorStatus !== config?.indicator_status) {
            await rpc.updateIndicatorStatus(indicatorStatus)
        }
        if (screenBrightness !== screen?.screen_standard_brightness) {
            await rpc
                .updateScreenBrightness(screenBrightness)
                .then(() =>
                    dispatch(
                        setEntityListAction({...screen, screen_standard_brightness: screenBrightness}, "screenState")
                    )
                )
                .catch(failure)
        }
        if (screenSaverActiveBrightness !== screen?.screen_saver_active_brightness) {
            await rpc
                .updateScreenSleepBrightness(screenSaverActiveBrightness)
                .then(() =>
                    dispatch(
                        setEntityListAction(
                            {...screen, screen_saver_active_brightness: screenSaverActiveBrightness},
                            "screenState"
                        )
                    )
                )
                .catch(failure)
        }
        if (selectScreenTime?.value !== screen?.screen_lock_timing_minute) {
            await rpc
                .updateScreenPinCodeTime(selectScreenTime?.value)
                .then(() =>
                    dispatch(
                        setEntityListAction(
                            {...screen, screen_lock_timing_minute: selectScreenTime?.value},
                            "screenState"
                        )
                    )
                )
                .catch(failure)
        }
        if (selectScreenSleepTime?.value !== screen?.screen_sleep_second) {
            await rpc
                .updateScreenSleepTime(selectScreenSleepTime?.value)
                .then(() =>
                    dispatch(
                        setEntityListAction(
                            {...screen, screen_sleep_second: selectScreenSleepTime?.value},
                            "screenState"
                        )
                    )
                )
                .catch(failure)
        }
        if (selectScreenType?.value !== screen?.screen_saver_type) {
            await rpc
                .updateScreenSaverType(selectScreenType?.value)
                .then(() =>
                    dispatch(
                        setEntityListAction({...screen, screen_saver_type: selectScreenType?.value}, "screenState")
                    )
                )
                .catch(failure)
        }
        if (selectScreenSaverTime?.value !== screen?.screen_saver_timing_minute) {
            await rpc
                .updateScreenSaverTime(selectScreenSaverTime?.value)
                .then(() =>
                    dispatch(
                        setEntityListAction(
                            {...screen, screen_saver_timing_minute: selectScreenSaverTime?.value},
                            "screenState"
                        )
                    )
                )
                .catch(failure)
        }
        if (pinCode !== screen?.screen_lock_status) {
            await rpc
                .updateScreenPinCodeState(pinCode)
                .then(() => dispatch(setEntityListAction({...screen, screen_lock_status: pinCode}, "screenState")))
                .catch(failure)
        }
        if (screenSaver !== screen?.screen_saver_status) {
            await rpc
                .updateScreenSaverState(screenSaver)
                .then(() => dispatch(setEntityListAction({...screen, screen_saver_status: screenSaver}, "screenState")))
                .catch(failure)
        }
    }, [
        failure,
        dispatch,
        screen,
        screenBrightness,
        favoritesItems,
        favoritesStatus,
        screenSaverActiveBrightness,
        rpc,
        indicatorStatus,
        config,
        selectScreenSleepTime?.value,
        selectScreenTime?.value,
        selectScreenType?.value,
        selectScreenSaverTime?.value,
        pinCode,
        screenSaver
    ])

    const onSaveChanges = useCallback(
        () =>
            question(t("customizations.do_you_confirm_the_screen_changes"), () => {
                askSaveChanges().then(() => {
                    dispatch(changeBusyAction(true))
                    rpc.manageGateway(selectedGateway.id, GatewayCommand.RestartWorker).catch(() =>
                        changeBusyAction(false)
                    )
                })
                const timer = setTimeout(() => {
                    changeBusyAction(false)
                    window.location.reload()
                }, 4500)
                return () => clearInterval(timer)
            }),
        [rpc, selectedGateway, dispatch, t, question, askSaveChanges]
    )

    const changeControl = useMemo(() => {
        if (
            favoritesItems === favoritesStatus &&
            indicatorStatus === config.indicator_status &&
            screenBrightness === screen?.screen_standard_brightness &&
            screenSaverActiveBrightness === screen?.screen_saver_active_brightness &&
            selectScreenTime?.value === screen?.screen_lock_timing_minute &&
            selectScreenSleepTime?.value === screen?.screen_sleep_second &&
            selectScreenType?.value === screen?.screen_saver_type &&
            selectScreenSaverTime?.value === screen?.screen_saver_timing_minute &&
            pinCode === screen?.screen_lock_status &&
            screenSaver === screen?.screen_saver_status
        ) {
            return false
        }
        return true
    }, [
        screen,
        screenBrightness,
        indicatorStatus,
        config,
        screenSaverActiveBrightness,
        selectScreenSleepTime,
        selectScreenTime,
        favoritesItems,
        favoritesStatus,
        selectScreenType,
        selectScreenSaverTime,
        pinCode,
        screenSaver,
    ])

    const buttons = useMemo(
        () => [
            {
                text: t("save"),
                onClick: onSaveChanges,
                className: "green",
                hidden: !changeControl
            }
        ],
        [onSaveChanges, t, changeControl]
    )

    return (
        <PasswordContainer requiredRole="default" isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} className="service-edit" backUrl={"/settings/custom-menu"}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingCheckBoxComponent
                            label={t("customizations.indicator_status")}
                            set={onChangeIndicatorStatus}
                            value={indicatorStatus}
                        />
                        {renderIf(currentRole !== "default" && currentRole !== "user")(() => (
                            <SettingCheckBoxComponent
                                label={t("customizations.favorites_status")}
                                set={onChangeFavoritesStatus}
                                value={favoritesItems}
                            />
                        ))}
                        <SettingCheckBoxComponent
                            label={t("customizations.screen_saver")}
                            set={onScreenSaverStateChange}
                            value={screenSaver}
                        />
                        <SettingCheckBoxComponent
                            label={t("customizations.screen_lock")}
                            set={onScreenPinCodeStateChange}
                            value={pinCode}
                        />
                        <SettingSelectComponent
                            label={t("customizations.screen_sleep_timing")}
                            options={screenSleepTimes}
                            value={selectScreenSleepTime}
                            onChange={setSelectScreenSleepTime}
                        />
                        <SettingSelectComponent
                            label={t("customizations.screen_saver_timing")}
                            options={saverTimeList}
                            value={selectScreenSaverTime}
                            onChange={setSelectScreenSaverTime}
                        />
                        <SettingSelectComponent
                            label={t("customizations.screen_saver_type")}
                            options={optionWallpaper}
                            value={selectScreenType}
                            onChange={setSelectScreenType}
                        />
                        <SettingSelectComponent
                            label={t("customizations.lock_timing")}
                            options={saverTimeList}
                            value={selectScreenTime}
                            onChange={setSelectScreenTime}
                        />
                    </div>
                    <div className="content-wrapper">
                        <CustomizedRange
                            columnProps={{min: 12}}
                            name={t("customizations.screen_brightness")}
                            value={screenBrightness}
                            onChange={onScreenBrightnessChange}
                            activeImage={iconScreen}
                            passiveImage={iconScreen}
                            minValue={10}
                            maxValue={100}
                            stepValue={5}
                            valueType={""}
                        />
                        <CustomizedRange
                            columnProps={{min: 12}}
                            name={t("customizations.screen_sleep_brightness")}
                            value={screenSaverActiveBrightness}
                            onChange={onScreenSleepBrightnessChange}
                            activeImage={iconScreenSleep}
                            passiveImage={iconScreenSleep}
                            minValue={0}
                            maxValue={100}
                            stepValue={5}
                            valueType={""}
                        />
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
